'use client';

import {
   type CSSProperties,
   type FocusEvent,
   type KeyboardEvent,
   type ReactNode,
   type RefObject,
   useEffect,
   useLayoutEffect,
   useRef,
   useState,
   useSyncExternalStore,
} from 'react';
import { useSwipe } from '../hooks/use-swipe';
import { afterExit } from '../lib/after-exit';
import { isKeyboardFocus } from '../lib/is-keyboard-focus';
import { cn } from '../lib/utils';
import { FaIcon } from './icon';

export type ToastPosition =
   | 'top-left'
   | 'top-center'
   | 'top-right'
   | 'bottom-left'
   | 'bottom-center'
   | 'bottom-right';

const POSITIONS: ToastPosition[] = [
   'top-left',
   'top-center',
   'top-right',
   'bottom-left',
   'bottom-center',
   'bottom-right',
];

type ToastId = string | number;
type ToastVariant = 'default' | 'error' | 'info' | 'success' | 'warning';

interface ToastAction {
   label: string;
   onClick: () => void;
}

interface ToastProps {
   id: ToastId;
   title: string;
   description?: string;
   action?: ToastAction;
   cancel?: ToastAction;
   /**
    * Picks the colored status icon beside the text (none for `default`) and
    * how the toast is announced: an error interrupts a screen reader, the
    * rest wait their turn.
    */
   variant?: ToastVariant;
   /** Takes the status icon's place, for a message with a mark of its own. */
   icon?: ReactNode;
   /** Milliseconds on screen; `Infinity` stays until dismissed. */
   duration?: number;
   'data-testid'?: string;
}

type ToastOptions = Omit<ToastProps, 'id' | 'title' | 'variant'> & {
   id?: ToastId;
   position?: ToastPosition;
   /**
    * The toast closed on its own: its time ran out, or a newer toast pushed
    * it past the Toaster's visible cap.
    */
   onAutoClose?: () => void;
   /** The person closed it: the X, Escape, a swipe, an action, or `toast.dismiss`. */
   onDismiss?: () => void;
};

type CustomToastProps = Omit<ToastProps, 'id'> &
   Pick<ToastOptions, 'position' | 'onAutoClose' | 'onDismiss'>;

export interface ToasterProps {
   /** Corner the toasts land in unless a toast asks for its own. */
   position?: ToastPosition;
   /** Default time on screen for a toast that sets no duration. */
   duration?: number;
   /** Toasts on screen per corner; one more closes the oldest, as shadcn's `limit` does. */
   visibleToasts?: number;
   /** Accessible name of each toast's close button. */
   closeLabel?: string;
   /** Accessible name of the region the F8 hotkey focuses. */
   regionLabel?: string;
   className?: string;
}

export const toast = {
   custom: (props: CustomToastProps) => show(props.variant ?? 'default', props.title, props),
   error: (title: string, options?: ToastOptions) => show('error', title, options),
   info: (title: string, options?: ToastOptions) => show('info', title, options),
   success: (title: string, options?: ToastOptions) => show('success', title, options),
   warning: (title: string, options?: ToastOptions) => show('warning', title, options),
   /** Closes one toast, or every toast when called without an id. */
   dismiss: (id?: ToastId) => close(id, 'dismiss'),
};

// A page whose toasts belong in a different corner from the Toaster's own binds
// the position once here, so every call site keeps reading `toast.success(...)`
// and a new one cannot forget it. The single Toaster on the page keeps one list
// per corner, so it still shows them where the page asked.
export function toastAt(position: ToastPosition) {
   return {
      dismiss: toast.dismiss,
      custom: (props: Omit<CustomToastProps, 'position'>) => toast.custom({ ...props, position }),
      error: (title: string, options?: ToastOptions) =>
         toast.error(title, { ...options, position }),
      info: (title: string, options?: ToastOptions) => toast.info(title, { ...options, position }),
      success: (title: string, options?: ToastOptions) =>
         toast.success(title, { ...options, position }),
      warning: (title: string, options?: ToastOptions) =>
         toast.warning(title, { ...options, position }),
   };
}

/**
 * The one place a page's toasts render. Mount it once; a second Toaster draws
 * every toast twice. Toasts raised before it mounts wait in the queue and show
 * when it does.
 */
export function Toaster({
   position = 'bottom-right',
   duration = DEFAULT_DURATION,
   visibleToasts = 3,
   closeLabel = 'Close',
   regionLabel = 'Notifications (F8)',
   className,
}: ToasterProps) {
   const records = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
   const announcements = useAnnouncements(records);
   useToasterMounted(position, visibleToasts);
   useRegionHotkey();

   const byPosition = new Map<ToastPosition, ToastRecord[]>();
   for (const record of records) {
      const corner = record.position ?? position;
      byPosition.set(corner, [...(byPosition.get(corner) ?? []), record]);
   }
   // Corners keep a fixed DOM order: moving a region's element would replay
   // its cards' animations and drop the focus inside it. F8 finds the corner
   // that got the newest toast by its marker instead.
   const newestRevision = (toasts: ToastRecord[]) => Math.max(...toasts.map(t => t.revision));
   const corners = [...byPosition].sort(([a], [b]) => POSITIONS.indexOf(a) - POSITIONS.indexOf(b));
   const newestCorner = corners
      .map(([corner, toasts]) => ({ corner, revision: newestRevision(toasts) }))
      .sort((a, b) => b.revision - a.revision)[0]?.corner;

   return (
      <>
         <LiveRegions announcements={announcements} />
         {corners.map(([corner, toasts]) => (
            <ToastRegion
               key={corner}
               position={corner}
               isNewest={corner === newestCorner}
               toasts={toasts}
               defaultDuration={duration}
               closeLabel={closeLabel}
               regionLabel={regionLabel}
               className={className}
            />
         ))}
      </>
   );
}

interface ToastRegionProps {
   position: ToastPosition;
   /** The corner that got the newest toast; F8 lands here. */
   isNewest: boolean;
   toasts: ToastRecord[];
   defaultDuration: number;
   closeLabel: string;
   regionLabel: string;
   className?: string;
}

function ToastRegion({
   position,
   isNewest,
   toasts,
   defaultDuration,
   closeLabel,
   regionLabel,
   className,
}: ToastRegionProps) {
   const [y, x] = position.split('-') as ['top' | 'bottom', 'left' | 'center' | 'right'];
   const regionRef = useRef<HTMLDivElement>(null);
   const returnFocusRef = useRef<HTMLElement | null>(null);
   const [isHovered, setIsHovered] = useState(false);
   const [isFocused, setIsFocused] = useState(false);
   const isTapped = useTapToExpand(regionRef);
   const isPageHidden = usePageHidden();
   const isExpanded = isHovered || isFocused || isTapped;
   const isPaused = isExpanded || isPageHidden;
   const [heights, setHeights] = useState(() => new Map<ToastId, number>());
   const stack = layoutStack(toasts, heights);

   const recordHeight = (id: ToastId, px: number | null) => {
      setHeights(current => {
         if (px === null ? !current.has(id) : current.get(id) === px) return current;
         const next = new Map(current);
         if (px === null) next.delete(id);
         else next.set(id, px);
         return next;
      });
   };

   const isOutside = (node: EventTarget | null) =>
      !(node instanceof Node && regionRef.current?.contains(node));

   const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
      if (isOutside(event.relatedTarget)) {
         returnFocusRef.current =
            event.relatedTarget instanceof HTMLElement ? event.relatedTarget : null;
      }
      // A click focuses a button too, and releaseFocus keeps that focus in
      // the region, so pointer focus must not hold the stack open.
      if (isKeyboardFocus(event.target)) setIsFocused(true);
   };

   const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
      if (isOutside(event.relatedTarget)) setIsFocused(false);
   };

   const frontToast = () => {
      const open = regionRef.current?.querySelectorAll<HTMLElement>('li[data-state="open"]');
      return open ? (Array.from(open).at(-1) ?? null) : null;
   };

   // Escape with the region itself focused (right after F8, before a Tab)
   // closes the front toast; a toast's own Escape handler covers the rest.
   const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape' || event.target !== event.currentTarget) return;
      const front = [...toasts].reverse().find(t => t.open);
      if (!front) return;
      event.stopPropagation();
      close(front.id, 'dismiss');
   };

   // A toast that leaves with focus inside it hands focus to the front toast,
   // else back to where it was before the list was entered. An action that
   // moved focus itself (opening a drawer) has already taken it elsewhere,
   // so nothing here undoes that.
   const releaseFocus = (leaving: HTMLLIElement) => {
      if (!leaving.contains(document.activeElement)) return;
      const target = frontToast() ?? returnFocusRef.current;
      if (target?.isConnected) {
         target.focus();
      } else {
         (document.activeElement as HTMLElement | null)?.blur();
      }
   };

   return (
      <div
         ref={regionRef}
         role="region"
         aria-label={regionLabel}
         tabIndex={-1}
         data-slot="toast-region"
         data-position-y={y}
         data-position-x={x}
         data-newest={isNewest ? '' : undefined}
         data-expanded={isExpanded ? '' : undefined}
         className={cn(className)}
         style={stack.style}
         onMouseEnter={() => setIsHovered(true)}
         onMouseLeave={() => setIsHovered(false)}
         onFocus={handleFocus}
         onBlur={handleBlur}
         onKeyDown={handleKeyDown}
      >
         <ol data-slot="toast-list">
            {toasts.map(record => (
               <ToastItem
                  key={record.id}
                  record={record}
                  duration={record.duration ?? defaultDuration}
                  isPaused={isPaused}
                  isBehind={!isExpanded && stack.indexOf(record.id) > 0}
                  style={stack.styleOf(record.id)}
                  swipeDirection={x === 'center' ? 'both' : x}
                  closeLabel={closeLabel}
                  onHeight={recordHeight}
                  onRelease={releaseFocus}
               />
            ))}
         </ol>
      </div>
   );
}

interface ToastItemProps {
   record: ToastRecord;
   duration: number;
   isPaused: boolean;
   /**
    * Collapsed behind the front toast: only its edge shows, and its text and
    * controls are hidden and inert, so a tap on the peek reaches the card
    * (which fans the stack out) and not an invisible button.
    */
   isBehind: boolean;
   style: CSSProperties;
   swipeDirection: 'left' | 'right' | 'both';
   closeLabel: string;
   onHeight: (id: ToastId, px: number | null) => void;
   onRelease: (element: HTMLLIElement) => void;
}

function ToastItem({
   record,
   duration,
   isPaused,
   isBehind,
   style,
   swipeDirection,
   closeLabel,
   onHeight,
   onRelease,
}: ToastItemProps) {
   const { id, title, description, action, cancel, variant, icon, open, revision } = record;
   const ref = useRef<HTMLLIElement>(null);
   useNaturalHeight(ref, id, onHeight);
   // A closing toast ignores a second press: the pointer is already blocked
   // by toast.css, and a focused button would otherwise run its action twice.
   const dismiss = () => {
      if (open) close(id, 'dismiss');
   };
   const swipe = useSwipe(swipeDirection, revision, dismiss);
   useAutoClose(id, revision, duration, open && !isPaused);
   useRemoveAfterExit(ref, record, onRelease);

   const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      dismiss();
   };

   const run = (button: ToastAction) => () => {
      if (!open) return;
      button.onClick();
      close(id, 'dismiss');
   };

   return (
      <li
         ref={ref}
         role="status"
         aria-live="off"
         aria-atomic="true"
         tabIndex={0}
         data-slot="toast"
         data-state={open ? 'open' : 'closed'}
         data-variant={variant}
         data-behind={isBehind ? '' : undefined}
         data-testid={record.testId}
         onKeyDown={handleKeyDown}
         {...swipe.handlers}
         style={{ ...style, '--toast-swipe-x': `${swipe.offsetPx}px` } as CSSProperties}
      >
         <div data-slot="toast-body" inert={isBehind}>
            <div data-slot="toast-content">
               <ToastIcon variant={variant} icon={icon} />
               <div data-slot="toast-text">
                  <div data-slot="toast-title">{title}</div>
                  {description && <div data-slot="toast-description">{description}</div>}
               </div>
            </div>
            {(action || cancel) && (
               <div data-slot="toast-actions">
                  {cancel && (
                     <button type="button" className="button-ghost button-xs" onClick={run(cancel)}>
                        {cancel.label}
                     </button>
                  )}
                  {action && (
                     <button
                        type="button"
                        className="button-outline button-xs"
                        onClick={run(action)}
                     >
                        {action.label}
                     </button>
                  )}
               </div>
            )}
         </div>
         <button
            type="button"
            data-slot="toast-close"
            className="button-ghost button-xs"
            aria-label={closeLabel}
            inert={isBehind}
            onClick={dismiss}
         >
            <FaIcon name="xmark" />
         </button>
      </li>
   );
}

// shadcn's status icons in Font Awesome; toast.css colors each one.
const VARIANT_ICONS: Record<Exclude<ToastVariant, 'default'>, string> = {
   success: 'circle-check',
   info: 'circle-info',
   warning: 'triangle-exclamation',
   error: 'octagon-xmark',
};

function ToastIcon({ variant, icon }: { variant: ToastVariant; icon?: ReactNode }) {
   if (icon) {
      return (
         <span data-slot="toast-icon" data-own-icon="" aria-hidden="true">
            {icon}
         </span>
      );
   }
   if (variant === 'default') return null;
   return (
      <span data-slot="toast-icon" aria-hidden="true">
         <FaIcon name={VARIANT_ICONS[variant]} />
      </span>
   );
}

interface Announcement {
   key: string;
   text: string;
   isAssertive: boolean;
}

// The toasts themselves are `aria-live="off"`: one hidden live region per
// politeness announces each toast once. The region is not atomic and only
// reports additions, so a second toast arriving while the first's text is
// still there reads the new text alone; each entry is atomic so its title and
// description read as one sentence.
function LiveRegions({ announcements }: { announcements: Announcement[] }) {
   return (
      <>
         <div aria-live="polite" aria-atomic="false" aria-relevant="additions" className="sr-only">
            {announcements
               .filter(a => !a.isAssertive)
               .map(a => (
                  <div key={a.key} aria-atomic="true">
                     {a.text}
                  </div>
               ))}
         </div>
         <div
            aria-live="assertive"
            aria-atomic="false"
            aria-relevant="additions"
            className="sr-only"
         >
            {announcements
               .filter(a => a.isAssertive)
               .map(a => (
                  <div key={a.key} aria-atomic="true">
                     {a.text}
                  </div>
               ))}
         </div>
      </>
   );
}

const ANNOUNCEMENT_MS = 1000;

function useAnnouncements(records: ToastRecord[]) {
   const [announcements, setAnnouncements] = useState<Announcement[]>([]);
   const announcedRef = useRef(new Map<ToastId, number>());
   const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>());

   useEffect(() => {
      const announced = announcedRef.current;
      const live = new Set(records.map(r => r.id));
      for (const id of announced.keys()) if (!live.has(id)) announced.delete(id);

      const fresh = records.filter(r => r.open && announced.get(r.id) !== r.revision);
      if (fresh.length === 0) return;
      const added = fresh.map(r => {
         announced.set(r.id, r.revision);
         return {
            key: `${r.id}:${r.revision}`,
            text: r.description ? `${r.title} ${r.description}` : r.title,
            isAssertive: r.variant === 'error',
         };
      });
      const keys = new Set(added.map(a => a.key));
      // Keyed, not by identity, so an effect replayed by Strict Mode replaces
      // its own entries instead of doubling them.
      setAnnouncements(current => [...current.filter(a => !keys.has(a.key)), ...added]);
      // Cleared on its own clock, not this effect's cleanup, so a burst of
      // toasts cannot cut an earlier announcement short.
      const timers = timersRef.current;
      const timer = setTimeout(() => {
         timers.delete(timer);
         setAnnouncements(current => current.filter(a => !keys.has(a.key)));
      }, ANNOUNCEMENT_MS);
      timers.add(timer);
   }, [records]);

   // Unmount forgets what was announced along with the timers, so a replayed
   // mount announces again and re-arms the clear that the cleanup cancelled.
   useEffect(() => {
      const timers = timersRef.current;
      const announced = announcedRef.current;
      return () => {
         for (const timer of timers) clearTimeout(timer);
         timers.clear();
         announced.clear();
      };
   }, []);

   return announcements;
}

// Radix's convention: F8 jumps to the notifications so a keyboard user can
// reach an action before the toast times out (hovering pauses it for a mouse).
function useRegionHotkey() {
   useEffect(() => {
      const onKeyDown = (event: globalThis.KeyboardEvent) => {
         if (event.key !== 'F8') return;
         const region = document.querySelector<HTMLElement>(
            '[data-slot="toast-region"][data-newest]'
         );
         if (!region) return;
         event.preventDefault();
         region.focus();
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, []);
}

function useAutoClose(id: ToastId, revision: number, duration: number, isRunning: boolean) {
   const remainingMsRef = useRef(duration);
   const startedAtMsRef = useRef(0);

   // A re-fired toast starts its time over: declared before the timer effect
   // so the reset lands after the old timer's cleanup and before the new start.
   useEffect(() => {
      remainingMsRef.current = duration;
   }, [revision, duration]);

   useEffect(() => {
      if (!isRunning || !Number.isFinite(duration)) return;
      startedAtMsRef.current = Date.now();
      const timer = setTimeout(() => close(id, 'timeout'), remainingMsRef.current);
      return () => {
         clearTimeout(timer);
         remainingMsRef.current -= Date.now() - startedAtMsRef.current;
      };
   }, [id, revision, duration, isRunning]);
}

// The closed toast plays its exit animation, then leaves the store. A toast
// re-fired mid-exit reopens instead, and the cleanup disarms this run.
function useRemoveAfterExit(
   ref: RefObject<HTMLLIElement | null>,
   record: ToastRecord,
   onRelease: (element: HTMLLIElement) => void
) {
   const { id, open } = record;
   useEffect(() => {
      if (open) return;
      const element = ref.current;
      return afterExit(element, () => {
         if (element) onRelease(element);
         remove(id);
      });
      // onRelease reads the live list at call time; it never needs a re-run.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id, open, ref]);
}

function usePageHidden() {
   return useSyncExternalStore(
      onChange => {
         document.addEventListener('visibilitychange', onChange);
         return () => document.removeEventListener('visibilitychange', onChange);
      },
      () => document.hidden,
      () => false
   );
}

// The store learns the Toaster's corner and cap so `show` can close the
// overflow the moment a toast is raised, with no frame where four are up.
// Toasts raised before the mount are trimmed to the cap on mount.
function useToasterMounted(position: ToastPosition, visibleToasts: number) {
   useEffect(() => {
      const toaster: ToasterConfig = { position, visibleToasts };
      store.toasters.push(toaster);
      closeOverflow();
      return () => {
         store.toasters.splice(store.toasters.indexOf(toaster), 1);
      };
   }, [position, visibleToasts]);
}

const TAP_SLOP_PX = 8;
const TAP_CLICK_WINDOW_MS = 500;

// On a phone there is no hover, so a tap on the stack fans it out and a tap
// anywhere else folds it. The tap is decided on pointer-up, once the finger
// has proven it did not move: a swipe or a scroll that starts on the stack is
// not a tap. A tap on a button is that button's alone. The click the browser
// synthesizes after the tap is swallowed: by then the fan-out has moved the
// cards and re-enabled their buttons, and one of those can sit under the
// finger.
function useTapToExpand(regionRef: RefObject<HTMLDivElement | null>) {
   const [isTapped, setIsTapped] = useState(false);
   const touchStartRef = useRef<{ x: number; y: number } | null>(null);
   const swallowClickUntilRef = useRef(0);

   useEffect(() => {
      const onPointerDown = (event: globalThis.PointerEvent) => {
         if (event.pointerType !== 'touch') return;
         const target = event.target as Element | null;
         if (!regionRef.current?.contains(target)) {
            touchStartRef.current = null;
            setIsTapped(false);
            return;
         }
         touchStartRef.current = target?.closest('button, a')
            ? null
            : { x: event.clientX, y: event.clientY };
      };
      const onPointerUp = (event: globalThis.PointerEvent) => {
         const start = touchStartRef.current;
         touchStartRef.current = null;
         if (!start || event.pointerType !== 'touch') return;
         const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
         if (moved >= TAP_SLOP_PX) return;
         swallowClickUntilRef.current = Date.now() + TAP_CLICK_WINDOW_MS;
         setIsTapped(current => !current);
      };
      const onPointerCancel = () => {
         touchStartRef.current = null;
      };
      const onClick = (event: MouseEvent) => {
         if (Date.now() > swallowClickUntilRef.current) return;
         swallowClickUntilRef.current = 0;
         if (!regionRef.current?.contains(event.target as Node)) return;
         event.stopPropagation();
         event.preventDefault();
      };
      document.addEventListener('pointerdown', onPointerDown);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerCancel);
      document.addEventListener('click', onClick, { capture: true });
      return () => {
         document.removeEventListener('pointerdown', onPointerDown);
         document.removeEventListener('pointerup', onPointerUp);
         document.removeEventListener('pointercancel', onPointerCancel);
         document.removeEventListener('click', onClick, { capture: true });
      };
   }, [regionRef]);

   return isTapped;
}

// The stack sets each card's height from the front card's, so the card's own
// height is rebuilt from what is inside it: the tallest child plus the card's
// padding and borders. The children are watched, not the card, since the box
// stays put when the text inside changes.
function useNaturalHeight(
   ref: RefObject<HTMLLIElement | null>,
   id: ToastId,
   onHeight: (id: ToastId, px: number | null) => void
) {
   useLayoutEffect(() => {
      const element = ref.current;
      if (!element) return;
      const measure = () => {
         const style = getComputedStyle(element);
         const frame =
            Number.parseFloat(style.paddingTop) +
            Number.parseFloat(style.paddingBottom) +
            Number.parseFloat(style.borderTopWidth) +
            Number.parseFloat(style.borderBottomWidth);
         const content = Math.max(
            0,
            ...[...element.children].map(child => (child as HTMLElement).offsetHeight)
         );
         onHeight(id, content + (Number.isNaN(frame) ? 0 : frame));
      };
      measure();
      if (typeof ResizeObserver === 'undefined') return () => onHeight(id, null);
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      for (const child of element.children) observer.observe(child);
      return () => {
         observer.disconnect();
         onHeight(id, null);
      };
      // onHeight is remade each region render but only ever calls the state
      // setter, so the first one captured does the job; measuring reruns on
      // resize, not on renders.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id, ref]);
}

/* ---- Helpers ---- */

// Keep in step with `--toast-gap` in toast.css.
const STACK_GAP_PX = 12;

// The newest toast is index 0 at the edge; each older one sits behind it.
// Collapsed, every card takes the front card's height and peeks out above
// it, scaled down a step per index. Expanded, each card takes its own height
// and moves out by the heights of the cards in front of it plus the gaps.
// A closing toast keeps its place until it has left, so the cards behind it
// slide up once, not twice.
function layoutStack(toasts: ToastRecord[], heights: Map<ToastId, number>) {
   const newestFirst = [...toasts].reverse();
   const slots = new Map<ToastId, { index: number; offsetPx: number }>();
   let offsetPx = 0;
   for (const [index, record] of newestFirst.entries()) {
      slots.set(record.id, { index, offsetPx });
      offsetPx += (heights.get(record.id) ?? 0) + STACK_GAP_PX;
   }
   const frontHeight = newestFirst[0] ? heights.get(newestFirst[0].id) : undefined;
   const px = (value: number | undefined) => (value === undefined ? undefined : `${value}px`);
   return {
      style: {
         '--toast-front-height': px(frontHeight),
         '--toast-stack-height': px(Math.max(0, offsetPx - STACK_GAP_PX)),
      } as CSSProperties,
      indexOf: (id: ToastId) => slots.get(id)?.index ?? 0,
      styleOf: (id: ToastId) => {
         const slot = slots.get(id);
         return {
            '--toast-index': slot?.index ?? 0,
            '--toast-height': px(heights.get(id)),
            '--toast-offset-y': px(slot?.offsetPx ?? 0),
         } as CSSProperties;
      },
   };
}

/* ---- The queue every toast call writes to and every Toaster reads ---- */

interface ToastRecord {
   id: ToastId;
   title: string;
   description?: string;
   action?: ToastAction;
   cancel?: ToastAction;
   variant: ToastVariant;
   icon?: ReactNode;
   duration?: number;
   position?: ToastPosition;
   onAutoClose?: () => void;
   onDismiss?: () => void;
   testId?: string;
   open: boolean;
   /** Bumped when the same id is raised again, so timers and announcements restart. */
   revision: number;
}

interface ToasterConfig {
   position: ToastPosition;
   visibleToasts: number;
}

const DEFAULT_DURATION = 4000;
// Toasts raised before a Toaster mounts wait here; past this many the oldest
// is forgotten rather than letting a page with no Toaster grow a queue forever.
const MAX_QUEUED = 20;
const EMPTY: ToastRecord[] = [];

const store = {
   records: EMPTY,
   revision: 0,
   nextId: 1,
   toasters: [] as ToasterConfig[],
   listeners: new Set<() => void>(),
};

function subscribe(listener: () => void) {
   store.listeners.add(listener);
   return () => {
      store.listeners.delete(listener);
   };
}

function getSnapshot() {
   return store.records;
}

function getServerSnapshot() {
   return EMPTY;
}

function commit(next: ToastRecord[]) {
   store.records = next;
   for (const listener of store.listeners) listener();
}

function show(variant: ToastVariant, title: string, options?: ToastOptions): ToastId {
   const id = options?.id ?? store.nextId++;
   store.revision += 1;
   const record: ToastRecord = {
      id,
      title,
      description: options?.description,
      action: options?.action,
      cancel: options?.cancel,
      variant,
      icon: options?.icon,
      duration: options?.duration,
      position: options?.position,
      onAutoClose: options?.onAutoClose,
      onDismiss: options?.onDismiss,
      testId: options?.['data-testid'],
      open: true,
      revision: store.revision,
   };
   // Raising an id that is still on screen updates that toast in place and
   // restarts its clock; the order of calls in one tick is the order applied,
   // so a dismiss followed by a re-fire leaves the toast up. An id that is on
   // its way out comes back as the newest arrival, else its old place at the
   // back of the line would put it straight past the cap again.
   const { records } = store;
   const existing = records.find(r => r.id === id);
   if (existing?.open) {
      commit(records.map(r => (r.id === id ? record : r)));
   } else {
      const others = records.filter(r => r.id !== id);
      commit(
         store.toasters.length === 0 ? [...others, record].slice(-MAX_QUEUED) : [...others, record]
      );
   }
   closeOverflow();
   return id;
}

// Past the Toaster's cap the oldest open toast in that corner closes, the way
// shadcn's `limit` works: the newest are the ones on screen, and nothing
// waits hidden to reappear later.
function closeOverflow() {
   const toaster = store.toasters[0];
   if (!toaster) return;
   const openByCorner = new Map<ToastPosition, ToastRecord[]>();
   for (const record of store.records) {
      if (!record.open) continue;
      const corner = record.position ?? toaster.position;
      openByCorner.set(corner, [...(openByCorner.get(corner) ?? []), record]);
   }
   for (const open of openByCorner.values()) {
      const overflow = open.slice(0, Math.max(0, open.length - toaster.visibleToasts));
      for (const record of overflow) close(record.id, 'overflow');
   }
}

function close(id: ToastId | undefined, reason: 'dismiss' | 'timeout' | 'overflow') {
   const { records } = store;
   const closing = records.filter(r => r.open && (id === undefined || r.id === id));
   if (closing.length === 0) return;
   const closed = new Set(closing.map(r => r.id));
   // With no Toaster to play the exit, there is nothing to wait for.
   commit(
      store.toasters.length === 0
         ? records.filter(r => !closed.has(r.id))
         : records.map(r => (closed.has(r.id) ? { ...r, open: false } : r))
   );
   for (const r of closing) (reason === 'dismiss' ? r.onDismiss : r.onAutoClose)?.();
}

// Only a closed toast leaves; a toast re-fired mid-exit is open again and stays.
function remove(id: ToastId) {
   const { records } = store;
   if (records.every(r => r.id !== id || r.open)) return;
   commit(records.filter(r => r.id !== id));
}
